import type { SqlImport } from '@/services/db/sql-import/entity';
import type { SqlImportQueryWriteInput, SqlImportWriteInput } from '@/services/db/sql-import/mutations';
import type { SqlDatabaseType } from '@/services/db/sql-import-database/entity';
import type { SqlImportRelationship } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import { sqlImportToDraft, type SqlImportDraft } from '../new/draft';
import { relationshipForStep } from '../new/relationships';

/**
 * `import_status` is how the wizard saves or publishes a draft. A published import omits it, as
 * Ember did: sending `published` again would re-activate a paused sync, and the backend treats an
 * absent status as a plain update. A draft opened here keeps saving as a draft.
 */
function withImportStatus(sqlImport: Pick<SqlImport, 'is_draft'>, body: SqlImportWriteInput): SqlImportWriteInput {
  if (sqlImport.is_draft) {
    body.import_status = 'draft';
  }
  return body;
}

/**
 * The update body for a change to the name or description: the import as it stands now with the
 * new field over it, so an edit made on another tab meanwhile is not sent back stale.
 */
export function detailsWriteBody(
  sqlImport: SqlImport,
  fields: Partial<Pick<SqlImportDraft, 'name' | 'description'>>,
  type: SqlDatabaseType,
): SqlImportWriteInput {
  return withImportStatus(sqlImport, { ...sqlImportToDraft(sqlImport), ...fields, database_type: type });
}

/**
 * The update body for the Settings tab: the import as it stands now, with the settings from the
 * draft over it. The name, description, query and relationship query are taken from the import, not
 * the draft, so a rename made in the header while the tab was open survives the save. The sides of
 * a relationship the import owns (its object type, its identifiers) follow the new settings.
 */
export function settingsWriteBody(
  sqlImport: SqlImport,
  draft: SqlImportDraft,
  type: SqlDatabaseType,
): SqlImportWriteInput {
  const merged: SqlImportDraft = {
    ...sqlImportToDraft(sqlImport),
    interval: draft.interval,
    start_at: draft.start_at,
    import_type: draft.import_type,
    object_type_id: draft.object_type_id,
    id_field_name: draft.id_field_name,
    segment_id: draft.segment_id,
    segment_name: draft.segment_name,
    clear_empty_cells: draft.clear_empty_cells,
    ignore_suppress_err: draft.ignore_suppress_err,
    database_id: draft.database_id,
  };
  const relationship = merged.relationship == null ? undefined : relationshipForStep(merged, []);
  return withImportStatus(sqlImport, { ...merged, relationship, database_type: type });
}

/** Ember asked the customer to re-check the query after the identifier or the database moved. */
export function settingsNeedQueryReview(
  baseline: Pick<SqlImportDraft, 'id_field_name' | 'database_id'>,
  draft: Pick<SqlImportDraft, 'id_field_name' | 'database_id'>,
): boolean {
  return baseline.id_field_name !== draft.id_field_name || baseline.database_id !== draft.database_id;
}

/** The `update_query` body for the Query tab: the query, and the BigQuery location when there is one. */
export function queryWriteBody(draft: Pick<SqlImportDraft, 'query' | 'location'>): SqlImportQueryWriteInput {
  const body: SqlImportQueryWriteInput = { query: draft.query ?? '' };
  if (!isEmptyString(draft.location)) {
    body.location = draft.location;
  }
  return body;
}

/**
 * The `update_query` body for the Relationship Query tab. The endpoint takes the relationship's
 * query and people identifier; its object type and object identifier are fixed by the import.
 */
export function relationshipsQueryWriteBody(
  draft: Pick<SqlImportDraft, 'query' | 'location'>,
  relationship: Pick<SqlImportRelationship, 'query' | 'entity1_id_field_name'>,
): SqlImportQueryWriteInput {
  return {
    ...queryWriteBody(draft),
    relationship: { query: relationship.query, entity1_id_field_name: relationship.entity1_id_field_name },
  };
}
